/* Lack Depot — Gefahrgut surcharge auto-manager
   --------------------------------------------------
   When the customer's cart contains any product tagged "Gefahrgut",
   automatically add 1× of the "Gefahrgut-Aufschlag" surcharge product
   (€3.50). When no aerosol products remain in cart, auto-remove it.

   The surcharge variant ID is hard-coded below. The detection happens
   by fetching /products/{handle}.js for each cart item and checking
   the tags array. Results are cached in memory for the session. */

(function () {
  'use strict';

  const SURCHARGE_VARIANT_ID = 53889764622669;        // Gefahrgut-Aufschlag variant
  const SURCHARGE_HANDLE = 'gefahrgut-aufschlag';
  const TRIGGER_TAG = 'gefahrgut';                    // lower-cased for comparison

  // In-memory cache: { 'product-handle': true/false } for "has Gefahrgut tag"
  const tagCache = new Map();

  // Re-entrancy guard so the auto-add/remove doesn't loop on itself
  let busy = false;

  function isSurchargeHandle(handle) {
    return (handle || '').toLowerCase() === SURCHARGE_HANDLE;
  }

  /** Fetch a product's tags. Returns true if it has the Gefahrgut tag. */
  function productHasGefahrgut(handle) {
    if (!handle) return Promise.resolve(false);
    if (isSurchargeHandle(handle)) return Promise.resolve(false);
    if (tagCache.has(handle)) return Promise.resolve(tagCache.get(handle));

    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (product) {
        const tags = (product.tags || []).map(function (t) { return String(t).toLowerCase().trim(); });
        const hit = tags.indexOf(TRIGGER_TAG) !== -1;
        tagCache.set(handle, hit);
        return hit;
      })
      .catch(function () {
        tagCache.set(handle, false);
        return false;
      });
  }

  /** Get cart state and decide if surcharge should be present. */
  function checkCart() {
    if (busy) return Promise.resolve();

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) return;

        const surchargeItem = cart.items.find(function (i) { return i.variant_id === SURCHARGE_VARIANT_ID; });
        const realItems = cart.items.filter(function (i) { return i.variant_id !== SURCHARGE_VARIANT_ID; });

        if (realItems.length === 0) {
          // Empty cart (except possibly the surcharge) — remove surcharge if it's hanging around
          if (surchargeItem) return removeSurcharge();
          return;
        }

        // Check each unique handle in parallel
        const uniqueHandles = Array.from(new Set(realItems.map(function (i) { return i.handle; })));
        return Promise.all(uniqueHandles.map(productHasGefahrgut))
          .then(function (results) {
            const hasGefahrgut = results.some(function (r) { return r === true; });
            if (hasGefahrgut && !surchargeItem) return addSurcharge();
            if (!hasGefahrgut && surchargeItem) return removeSurcharge();
            // Already in correct state — no-op
          });
      })
      .catch(function (err) {
        console.warn('[Gefahrgut] checkCart failed', err);
      });
  }

  function addSurcharge() {
    busy = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: SURCHARGE_VARIANT_ID, quantity: 1 }] })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function () {
        busy = false;
        // Notify the cart UI to refresh
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(function () { busy = false; });
  }

  function removeSurcharge() {
    busy = true;
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: SURCHARGE_VARIANT_ID, quantity: 0 })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function () {
        busy = false;
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(function () { busy = false; });
  }

  /** Debounce to avoid hammering the cart API on rapid changes. */
  let debounceTimer = null;
  function scheduleCheck() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(checkCart, 300);
  }

  // 1. Intercept fetch() so any /cart/add|change|update|clear triggers a re-check
  if (window.fetch) {
    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = (typeof input === 'string') ? input : (input && input.url) || '';
      const promise = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/i.test(url) && !busy) {
        promise.then(function () { scheduleCheck(); }).catch(function () {});
      }
      return promise;
    };
  }

  // 2. Intercept XMLHttpRequest for older theme code paths
  if (window.XMLHttpRequest) {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.addEventListener('load', function () {
        if (/\/cart\/(add|change|update|clear)/i.test(url || '') && !busy) {
          scheduleCheck();
        }
      });
      return origOpen.apply(this, arguments);
    };
  }

  // 3. Initial check on page load
  function init() {
    // Defer slightly so other cart scripts have a chance to settle
    setTimeout(checkCart, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
